'use client';
import {
  AssociatedDealerDropdown,
  AssociatedDealerForm,
  AssociatedDealerInput,
  Conditional
} from 'components/pcc-components';
import {
  DEALER_DROPDOWN_NAME,
  DEALER_SEARCH_FORM_ID,
  CUSTOMER_NUMBER_INPUT_NAME,
  DEALER_INPUT_ADDRESS,
  DEALER_INPUT_CITY,
  DEALER_INPUT_ZIP_CODE,
  ERRORS,
  STATUS_FAIL,
  STATUS_SUCCESS,
  SEARCH_RESULTS_FORM_ID
} from '../../constants/constants';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { useTranslations } from 'next-intl';
import { ErrorMessageBanner } from '../error-message-banner';
import { useEffect, useMemo, useState } from 'react';
import { useAssociatedDealer } from 'components/associated-dealers/associated-dealer-context';
import { useDealerSearch } from 'components/associated-dealers/hooks/use-dealer-search';
import {
  DealerSearchFormData,
  FormType
} from 'components/associated-dealers/types';
import { CatLoadingIndicator } from 'blocks-nextjs/blocks-react/components/LoadingIndicator';
import { CatInlineNotification } from 'blocks-nextjs/blocks-react/components/InlineNotification';
import { CatIconWarningCircle } from 'blocks-nextjs/blocks-react/components/Icons/WarningCircle';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { useDealerCustomer } from 'components/associated-dealers/hooks/use-dealer-customer';
import { useFeedback } from 'providers/feedback-provider';
import { AssociatedDealerTitle } from '../associated-dealer-title';
import {
  AssociatedDealerBody,
  AssociatedDealerFooter,
  AssociatedDealerHeader
} from '../associated-dealer-parts';
import { DealerResponse } from 'services/dealer';

type DealerSearchFormProps = {
  dealerSearchForm: FormType;
  isModal?: boolean;
  dealerUserResponse: DealerResponse;
};

export const DealerSearchForm = ({
  dealerSearchForm,
  isModal = false,
  dealerUserResponse
}: DealerSearchFormProps) => {
  const { searchResultsDrawer, selectCustomerDrawer } = useAssociatedDealer();
  const { fireEvent } = useFormSubmitEvent();
  const { feedbackMessage, updateFeedback, dismissFeedback } = useFeedback();
  const [isSingleResult, setIsSingleResult] = useState(false);
  const t = useTranslations();

  const { startSearch, isFetched, isLoading, isError, data } =
    useDealerSearch();
  const {
    startSearch: startSearchStores,
    data: storesData,
    isFetched: storesFetched,
    isLoading: storesLoading,
    isError: storesError
  } = useDealerCustomer();

  const dealerValue = useMemo(() => {
    return [
      {
        label: dealerUserResponse?.name,
        value: dealerUserResponse.storeId
      }
    ];
  }, [dealerUserResponse.name, dealerUserResponse.storeId]);

  const isEveryFieldEmpty = (data) => {
    const fieldsArray = [
      CUSTOMER_NUMBER_INPUT_NAME,
      DEALER_INPUT_ADDRESS,
      DEALER_INPUT_CITY,
      DEALER_INPUT_ZIP_CODE
    ];
    return fieldsArray.every((field) => data?.[field] === '');
  };

  const validateDCN = () => {
    const isInvalid =
      !storesData?.dealerStoreLocations ||
      storesData?.dealerStoreLocations?.stores?.length < 1;
    return isInvalid;
  };

  const handleSubmit = () => {
    dealerSearchForm.handleSubmit((data: DealerSearchFormData) => {
      if (!isEveryFieldEmpty(data)) {
        startSearch(data);
        dismissFeedback();
      } else {
        updateFeedback(ERRORS.NO_SEARCH_VALUES);
      }
    })();
  };

  useEffect(() => {
    if (isFetched && !isError && data?.dealerCustomer?.length > 0) {
      dismissFeedback();
      fireEvent({
        formName: DEALER_SEARCH_FORM_ID,
        formStatus: STATUS_SUCCESS,
        formContent: dealerSearchForm.getValues()
      });

      if (data?.dealerCustomer?.length === 1) {
        setIsSingleResult(true);
        startSearchStores({
          customerNumber: data.dealerCustomer[0].customerNumber,
          storeId: ''
        });
      } else {
        setIsSingleResult(false);
        searchResultsDrawer.openDrawer();
      }
    } else if (
      (isFetched && data?.dealerCustomer?.length === 0) ||
      data?.dealerCustomer === null
    ) {
      fireEvent({ formName: DEALER_SEARCH_FORM_ID, formStatus: STATUS_FAIL });
      updateFeedback(ERRORS.NO_RESULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, isError, data]);

  useEffect(() => {
    if (!isSingleResult) return;
    if (storesFetched && !isError) {
      fireEvent({
        formStatus: STATUS_SUCCESS,
        formName: SEARCH_RESULTS_FORM_ID
      });
      const invalidDCN = validateDCN();
      if (invalidDCN) {
        updateFeedback(ERRORS.INVALID_DCN);
      } else {
        selectCustomerDrawer.openDrawer();
      }
    } else if (isError) {
      fireEvent({
        formStatus: STATUS_FAIL,
        formName: SEARCH_RESULTS_FORM_ID
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesFetched, storesError, storesData]);
  const emptyFieldsError = feedbackMessage === ERRORS.NO_SEARCH_VALUES;

  return (
    <>
      <AssociatedDealerHeader isModal={isModal}>
        <AssociatedDealerTitle>
          {t('CAT_MA_ASSOCIATEDDEALERS')}
        </AssociatedDealerTitle>
      </AssociatedDealerHeader>
      <AssociatedDealerBody isModal={isModal}>
        {isError && (
          <div className="mt-3 mb-3">
            <CatInlineNotification variant="error">
              <CatIconWarningCircle slot="before" data-testid="error-icon" />
              {t('VALID_DCN_NO_STORES')} {t('VALID_DCN_NO_STORES_SYS_ADMIN')}
            </CatInlineNotification>
          </div>
        )}
        <ErrorMessageBanner />
        <div className="mt-3"> {t('WILDCARD_SEARCH')} </div>
        <AssociatedDealerForm
          id={DEALER_SEARCH_FORM_ID}
          formMethods={dealerSearchForm}
          onSubmit={handleSubmit}
        >
          <div className="mt-3">
            <AssociatedDealerDropdown
              name={DEALER_DROPDOWN_NAME}
              placeholder={t('CAT_MA_SELECTDEALER')}
              label={t('Footer_Dealer')}
              itemList={dealerValue}
              readOnly={true}
              required={true}
            />
          </div>
          <div className="mt-3">
            <AssociatedDealerInput
              name={CUSTOMER_NUMBER_INPUT_NAME}
              placeholder={''}
              label={t('CUST_NAME_NUMBER_COMBINED')}
              isError={emptyFieldsError}
            />
          </div>
          <div className="mt-3">
            <AssociatedDealerInput
              name={DEALER_INPUT_ADDRESS}
              placeholder={''}
              label={t('ADDRESS_TITLE')}
              isError={emptyFieldsError}
            />
          </div>
          <div className="mt-3">
            <AssociatedDealerInput
              name={DEALER_INPUT_CITY}
              placeholder={''}
              label={t('CAT_EMAIL_CITY')}
              isError={emptyFieldsError}
            />
          </div>
          <div className="mt-3">
            <AssociatedDealerInput
              name={DEALER_INPUT_ZIP_CODE}
              placeholder={''}
              label={t('ZIPCODE_POSTALCODE')}
              isError={emptyFieldsError}
            />
          </div>
        </AssociatedDealerForm>
      </AssociatedDealerBody>
      <AssociatedDealerFooter isModal={isModal}>
        <CatButton
          variant="primary"
          onClick={handleSubmit}
          data-testid="search-dealer-btn"
        >
          <div className="d-flex align-items-center">
            {t('SEARCH_RESULTS_TEXT1')}
            <Conditional test={isLoading || storesLoading}>
              <CatLoadingIndicator
                className="ml-2"
                small={true}
                data-testid="search-dealer-loader"
              />
            </Conditional>
          </div>
        </CatButton>
      </AssociatedDealerFooter>
    </>
  );
};
